/**
 * Drawing of the pooled transient effects (effects.ts): floor rings and
 * cracks, air flashes and bolts, screen vignettes. Split from the event
 * dispatch so each file stays readable.
 */

import { elementColor, ELEMENT_SWATCH, rgba, UI } from '../palette.js';
import { circlePath, clamp01, drawGlow, easeOutCubic, hashRand, STAFF_TIP_H, TAU, UNIT, type Ctx } from './gfx.js';
import { drawElementMark } from './glyphs.js';
import type { Camera } from './camera.js';
import type { Effect } from './effects.js';

/** Floor-level effects (rings, cracks, wedges). */
export function drawEffectsGround(ctx: Ctx, cam: Camera, time: number, pool: readonly Effect[]): void {
  const u = UNIT;
  for (const e of pool) {
    if (!e.active) continue;
    const f = clamp01(e.t / e.dur);
    const px = cam.sx(e.x), py = cam.sy(e.y);
    switch (e.kind) {
      case 'nova': {
        const g = easeOutCubic(Math.min(1, e.t / 0.25));
        const r = e.r * u * g;
        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = rgba(e.color, 0.15 * (1 - f));
        ctx.beginPath(); ctx.ellipse(px, py, r, r * 0.45, 0, 0, TAU); ctx.fill();
        ctx.strokeStyle = rgba(e.color, 1 - f);
        ctx.lineWidth = 10 - 8 * g;
        ctx.stroke();
        if (e.el2 && e.t > 0.06) {
          const g2 = easeOutCubic(Math.min(1, (e.t - 0.06) / 0.25));
          ctx.strokeStyle = rgba(elementColor(e.el2), 1 - f);
          ctx.lineWidth = 3;
          ctx.beginPath(); ctx.ellipse(px, py, e.r * u * g2, e.r * u * g2 * 0.45, 0, 0, TAU); ctx.stroke();
        }
        ctx.globalCompositeOperation = 'source-over';
        break;
      }
      case 'quake': {
        const g = easeOutCubic(Math.min(1, e.t / 0.35));
        ctx.strokeStyle = rgba(ELEMENT_SWATCH.earth.core, 0.9 * (1 - f));
        for (let k = 0; k < 3; k++) {
          const r = e.r * u * g * (1 - k * 0.25);
          ctx.lineWidth = 3 - k * 0.6;
          ctx.beginPath();
          for (let i = 0; i < 36; i++) {
            const a = (TAU * i) / 36;
            const j = (hashRand(k * 100 + i, Math.floor(time * 20)) - 0.5) * 6;
            const x = px + Math.cos(a) * (r + j), y = py + Math.sin(a) * (r + j) * 0.45;
            if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          }
          ctx.closePath();
          ctx.stroke();
        }
        // cracks persist, fading
        ctx.strokeStyle = rgba('#000000', 0.6 * (1 - f));
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = (TAU * i) / 6 + hashRand(i, 3) * 0.6;
          let x = px, y = py;
          ctx.moveTo(x, y);
          for (let s = 1; s <= 4; s++) {
            const d = (e.r * u * g * s) / 4;
            x = px + Math.cos(a + (hashRand(i, s) - 0.5) * 0.5) * d;
            y = py + Math.sin(a + (hashRand(i, s) - 0.5) * 0.5) * d * 0.45;
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
        if (e.el2) {
          ctx.strokeStyle = rgba(elementColor(e.el2), 0.8 * (1 - f));
          ctx.lineWidth = 2;
          ctx.beginPath(); ctx.ellipse(px, py, e.r * u * g * 0.6, e.r * u * g * 0.27, 0, 0, TAU); ctx.stroke();
        }
        break;
      }
      case 'explosion': {
        const g = easeOutCubic(Math.min(1, e.t / 0.2));
        const r = e.r * u * g;
        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = rgba(e.color, 0.3 * (1 - f));
        ctx.beginPath(); ctx.ellipse(px, py, r, r * 0.45, 0, 0, TAU); ctx.fill();
        ctx.strokeStyle = rgba(e.color, 1 - f);
        ctx.lineWidth = 12 - 10 * g;
        ctx.stroke();
        if (e.el2) {
          ctx.strokeStyle = rgba(elementColor(e.el2), 1 - f);
          ctx.lineWidth = 4;
          ctx.beginPath(); ctx.ellipse(px, py, r * 0.8, r * 0.36, 0, 0, TAU); ctx.stroke();
        }
        ctx.globalCompositeOperation = 'source-over';
        break;
      }
      case 'wedge': {
        ctx.fillStyle = rgba(e.color, 0.5 * (1 - f));
        ctx.strokeStyle = rgba(e.color, 0.9 * (1 - f));
        ctx.lineWidth = 3;
        if (e.n === 0 || e.n === 3) {
          const r = (e.n === 0 ? 1.6 : 2) * u, half = (e.n === 0 ? 50 : 30) * Math.PI / 180;
          ctx.beginPath(); ctx.moveTo(px, py); ctx.ellipse(px, py, r, r * 0.45, 0, e.ang - half, e.ang + half); ctx.closePath();
          ctx.fill();
        } else {
          const len = (e.n === 1 ? 2.5 : 5) * u * (e.n === 2 ? easeOutCubic(Math.min(1, e.t / 0.3)) : 1);
          ctx.lineWidth = e.n === 2 ? 5 : 8;
          ctx.beginPath();
          ctx.moveTo(px, py);
          if (e.n === 2) {
            for (let s = 1; s <= 6; s++) {
              const d = (len * s) / 6;
              const j = (hashRand(s, 11) - 0.5) * 0.4 * u;
              ctx.lineTo(px + Math.cos(e.ang) * d - Math.sin(e.ang) * j, py + (Math.sin(e.ang) * d + Math.cos(e.ang) * j) * 0.45 + Math.sin(e.ang) * d * 0.55);
            }
          } else ctx.lineTo(px + Math.cos(e.ang) * len, py + Math.sin(e.ang) * len);
          ctx.stroke();
        }
        break;
      }
      case 'wind': {
        const r = e.r * u * (1 + 0.5 * f);
        ctx.strokeStyle = rgba(UI.white, 0.35 * (1 - f));
        ctx.lineWidth = 2;
        ctx.beginPath();
        if (e.n === 1) ctx.ellipse(px, py, r, r * 0.45, 0, 0, TAU);
        else { ctx.moveTo(px, py); ctx.ellipse(px, py, r, r * 0.45, 0, e.ang - 0.52, e.ang + 0.52); ctx.closePath(); }
        ctx.stroke();
        break;
      }
      case 'selfRing': {
        const r = (e.r - (e.r - 0.3) * easeOutCubic(f)) * u;
        ctx.strokeStyle = rgba(e.color, 1 - f * 0.5);
        ctx.lineWidth = 3;
        ctx.beginPath(); ctx.ellipse(px, py, r, r * 0.45, 0, 0, TAU); ctx.stroke();
        break;
      }
      case 'place': case 'magick': case 'deathRing': case 'shock': {
        const g = easeOutCubic(f);
        const r = e.r * u * (e.kind === 'place' ? 0.6 + 0.4 * g : g);
        ctx.strokeStyle = rgba(e.color, 1 - f);
        ctx.lineWidth = e.kind === 'magick' ? 5 : 3;
        ctx.beginPath(); ctx.ellipse(px, py, r, r * 0.45, 0, 0, TAU); ctx.stroke();
        if (e.kind === 'magick') { ctx.lineWidth = 2; ctx.beginPath(); ctx.ellipse(px, py, r * 0.6, r * 0.27, 0, 0, TAU); ctx.stroke(); }
        break;
      }
      case 'revive': {
        ctx.strokeStyle = rgba(e.color, 1 - f);
        ctx.lineWidth = 2.5;
        for (let k = 0; k < 3; k++) {
          const ff = clamp01(f * 1.5 - k * 0.25);
          if (ff <= 0) continue;
          const r = e.r * u * (1 - ff * 0.5);
          ctx.beginPath(); ctx.ellipse(px, py - ff * 1.2 * u, r, r * 0.45, 0, 0, TAU); ctx.stroke();
        }
        break;
      }
      default: break;
    }
  }
}

/** Above-ground effects (flashes, bolts, glyph pops, muzzle flashes). */
export function drawEffectsAir(ctx: Ctx, cam: Camera, time: number, pool: readonly Effect[]): void {
  const u = UNIT;
  ctx.globalCompositeOperation = 'lighter';
  for (const e of pool) {
    if (!e.active) continue;
    const f = clamp01(e.t / e.dur);
    const px = cam.sx(e.x), py = cam.sy(e.y);
    switch (e.kind) {
      case 'explosion':
        if (e.t < 0.06) { ctx.fillStyle = rgba(UI.white, 0.8); circlePath(ctx, px, py - 0.3 * u, e.r * u); ctx.fill(); }
        drawGlow(ctx, e.color, px, py - 0.3 * u, e.r * u * 3 * (1 - f * 0.5));
        break;
      case 'muzzle':
        drawGlow(ctx, e.color, cam.sx(e.x + Math.cos(e.ang) * STAFF_TIP_H), cam.sy(e.y + Math.sin(e.ang) * STAFF_TIP_H) - STAFF_TIP_H * u, 60 * (1 - f));
        break;
      case 'merge':
        drawGlow(ctx, UI.white, px, py - e.z * u, 80 * (1 - f));
        ctx.strokeStyle = rgba(UI.white, 1 - f);
        ctx.lineWidth = 2;
        circlePath(ctx, px, py - e.z * u, e.r * u * easeOutCubic(f));
        ctx.stroke();
        break;
      case 'shock':
        drawGlow(ctx, e.color, px, py - 0.5 * u, 90 * (1 - f));
        break;
      case 'bolt': {
        const frame = Math.floor(time * 30);
        const top = py - 14 * u;
        ctx.strokeStyle = rgba(e.color, 0.5 * (1 - f));
        ctx.lineWidth = 14;
        ctx.lineCap = 'round';
        const path = (): void => {
          ctx.beginPath();
          ctx.moveTo(px + (hashRand(frame, 0) - 0.5) * 3 * u, top);
          for (let i = 1; i < 8; i++) ctx.lineTo(px + (hashRand(frame, i) - 0.5) * 1.4 * u, top + ((py - top) * i) / 8);
          ctx.lineTo(px, py);
        };
        path(); ctx.stroke();
        ctx.strokeStyle = rgba(UI.white, 1 - f);
        ctx.lineWidth = 4;
        path(); ctx.stroke();
        if (e.t < 0.08) { ctx.fillStyle = rgba(UI.white, 0.6); circlePath(ctx, px, py, 2 * u); ctx.fill(); }
        drawGlow(ctx, e.color, px, py, 140 * (1 - f));
        break;
      }
      case 'glyph': {
        if (!e.el) break;
        const s = Math.sin(f * Math.PI);
        ctx.globalAlpha = s;
        ctx.fillStyle = e.color;
        drawElementMark(ctx, e.el, px, py - (e.z + f * 0.5) * u, 22 * (0.5 + s * 0.5));
        ctx.globalAlpha = 1;
        break;
      }
      default: break;
    }
  }
  ctx.globalCompositeOperation = 'source-over';
}

const vignetteCache = new Map<string, CanvasGradient>();

/** Radial gradient (transparent centre -> colour at the corners) cached per viewport size and colour. */
function vignetteGradient(ctx: Ctx, w: number, h: number, color: string): CanvasGradient {
  const key = `${w}x${h}${color}`;
  const hit = vignetteCache.get(key);
  if (hit) return hit;
  if (vignetteCache.size > 24) vignetteCache.clear();
  const cx = w / 2, cy = h / 2;
  const r = Math.hypot(cx, cy);
  const g = ctx.createRadialGradient(cx, cy, r * 0.35, cx, cy, r);
  g.addColorStop(0, rgba(color, 0));
  g.addColorStop(1, rgba(color, 1));
  vignetteCache.set(key, g);
  return g;
}

/** Screen-space vignettes (beam cross in both beams' colours, own death in red). */
export function drawEffectsScreen(ctx: Ctx, w: number, h: number, pool: readonly Effect[]): void {
  for (const e of pool) {
    if (!e.active || e.kind !== 'vignette') continue;
    const f = clamp01(e.t / e.dur);
    const a = 0.5 * (1 - f);
    ctx.globalAlpha = a;
    ctx.fillStyle = vignetteGradient(ctx, w, h, e.color);
    ctx.fillRect(0, 0, w, h);
    if (e.el2) {
      // second colour on the left/right thirds so a Water x Lightning cross reads as both
      ctx.globalAlpha = a * 0.7;
      ctx.fillStyle = vignetteGradient(ctx, w, h, elementColor(e.el2));
      ctx.fillRect(0, 0, w * 0.33, h);
      ctx.fillRect(w * 0.67, 0, w * 0.33, h);
    }
    ctx.globalAlpha = 1;
  }
}
